import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, Loader2, Plus, Trash2, Check, RefreshCw,
  Package, Apple, Wheat, Milk, Cookie, Sparkles, X, ChevronDown,
  Tag, MapPin, Store, Flame, Star
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth, useLanguage, api } from '../context/AppContext';

const CATEGORY_ICONS = {
  proteines: Package,
  legumes_fruits: Apple,
  cereales_feculents: Wheat,
  produits_laitiers: Milk,
  collations: Cookie,
  essentiels: Sparkles,
};

const CATEGORY_COLORS = {
  proteines: 'orange',
  legumes_fruits: 'emerald',
  cereales_feculents: 'amber',
  produits_laitiers: 'blue',
  collations: 'purple',
  essentiels: 'zinc',
};

export default function GroceryList() {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const fr = language === 'fr';

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [addingTo, setAddingTo] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [expandedCats, setExpandedCats] = useState({});
  const [deals, setDeals] = useState(null);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [showDeals, setShowDeals] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const res = await api.get('/grocery/list');
      setList(res.data.grocery_list);
      if (res.data.grocery_list) {
        const expanded = {};
        res.data.grocery_list.categories?.forEach(c => { expanded[c.id] = true; });
        setExpandedCats(expanded);
      }
    } catch { }
    finally { setLoading(false); }
  }, []);

  const fetchDeals = useCallback(async () => {
    setDealsLoading(true);
    try {
      const res = await api.get('/coach-deals/list');
      setDeals(res.data);
    } catch { }
    finally { setDealsLoading(false); }
  }, []);

  useEffect(() => { fetchList(); fetchDeals(); }, [fetchList, fetchDeals]);

  // Assistant IA deep-link: /grocery-list?action=add&item=saumon&qty=500g
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const action = sp.get('action');
    const item = sp.get('item');
    const qty = sp.get('qty');
    if (action === 'add' && item) {
      const label = qty ? `${item} ${qty}` : item;
      setNewItemName(label.slice(0, 80));
      // Expand the first category for quick adding
      if (list?.categories?.length) {
        setExpandedCats(prev => ({ ...prev, [list.categories[0].id]: true }));
        setAddingTo(list.categories[0].id);
      }
    }
    // Clean URL
    const next = new URLSearchParams(sp);
    ['action', 'item', 'qty'].forEach(k => next.delete(k));
    const newSearch = next.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, [list]);

  const generateList = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/grocery/generate');
      if (res.data.success) {
        setList(res.data.grocery_list);
        const expanded = {};
        res.data.grocery_list.categories?.forEach(c => { expanded[c.id] = true; });
        setExpandedCats(expanded);
        toast.success(t('pages.grocery_generated'));
      } else {
        toast.error(res.data.error || 'Error');
      }
    } catch (e) {
      const detail = e.response?.data?.error || e.response?.data?.detail || t('pages.grocery_gen_error');
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const toggleItem = async (itemId) => {
    try {
      const res = await api.put(`/grocery/list/${itemId}/toggle`);
      if (res.data.success) {
        setList(prev => {
          const updated = { ...prev };
          updated.categories = updated.categories.map(cat => ({
            ...cat,
            items: cat.items.map(item =>
              item.id === itemId ? { ...item, checked: res.data.checked } : item
            )
          }));
          return updated;
        });
      }
    } catch { }
  };

  const addItem = async (categoryId) => {
    if (!newItemName.trim()) return;
    try {
      const res = await api.post('/grocery/list/add', {
        category_id: categoryId,
        name: newItemName.trim(),
        quantity: 1,
        unit: '',
      });
      if (res.data.success) {
        setList(prev => {
          const updated = { ...prev };
          updated.categories = updated.categories.map(cat =>
            cat.id === categoryId
              ? { ...cat, items: [...cat.items, res.data.item] }
              : cat
          );
          return updated;
        });
        setNewItemName('');
        setAddingTo(null);
      }
    } catch { }
  };

  const deleteItem = async (itemId) => {
    try {
      const res = await api.delete(`/grocery/list/${itemId}`);
      if (res.data.success) {
        setList(prev => {
          const updated = { ...prev };
          updated.categories = updated.categories.map(cat => ({
            ...cat,
            items: cat.items.filter(i => i.id !== itemId)
          }));
          return updated;
        });
      }
    } catch { }
  };

  const totalItems = list?.categories?.reduce((sum, c) => sum + c.items.length, 0) || 0;
  const checkedItems = list?.categories?.reduce(
    (sum, c) => sum + c.items.filter(i => i.checked).length, 0
  ) || 0;
  const progress = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

  // 🍁 Quebec-only feature gate (per Yuri 2026-04-27)
  // Detect via the same logic as the backend: user_type === 'QUEBEC' OR service_tier === 'quebec'.
  const isQuebec = (
    String(user?.user_type || '').toUpperCase() === 'QUEBEC'
    || String(user?.service_tier || '').toLowerCase() === 'quebec'
  );

  if (!isQuebec) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-4 pb-24 md:pb-8" data-testid="grocery-list-page">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-heading text-3xl font-black uppercase tracking-tight">
                {t('pages.grocery_title')}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('pages.grocery_subtitle')}
              </p>
            </div>
            <ShoppingCart className="w-8 h-8 text-orange-500/40" />
          </div>
          <div
            className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-transparent p-8 text-center"
            data-testid="grocery-quebec-only-card"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-orange-500/15 mb-4">
              <MapPin className="w-10 h-10 text-orange-400" />
            </div>
            <h2 className="font-heading text-2xl uppercase tracking-tight text-foreground mb-2">
              {fr ? 'Disponible au Québec' : 'Available in Quebec'}
            </h2>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              {fr
                ? 'Le Panier Intelligent croise les rabais hebdo des épiceries québécoises (IGA, Métro, Maxi, Provigo) avec tes macros. On l\'ouvrira au reste du monde quand on aura intégré les chaînes locales.'
                : 'The Smart Cart cross-matches Quebec grocery weekly deals (IGA, Métro, Maxi, Provigo) with your macros. We\'ll open it to the rest of the world once we integrate local chains.'}
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/60 border border-border text-[11px] uppercase tracking-wider text-muted-foreground">
              <Sparkles className="w-3 h-3 text-orange-400" />
              {fr ? 'Bientôt à l\'international' : 'International coming soon'}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto p-4 pb-24 md:pb-8" data-testid="grocery-list-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-black uppercase tracking-tight">
              {fr ? 'Panier Intelligent' : 'Smart Cart'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {fr ? 'Liste optimisée par IA selon tes macros' : 'AI-optimized list based on your macros'}
            </p>
          </div>
          <ShoppingCart className="w-8 h-8 text-orange-500" />
        </div>

        {/* Generate / Regenerate Button */}
        <Button
          onClick={generateList}
          disabled={generating}
          className="w-full h-14 mb-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-base font-bold rounded-xl shadow-lg shadow-orange-500/20"
          data-testid="generate-list-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {fr ? 'Analyse de tes repas en cours...' : 'Analyzing your meals...'}
            </>
          ) : list ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2" />
              {fr ? 'Regenerer ma liste' : 'Regenerate my list'}
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              {fr ? 'Generer ma liste' : 'Generate my list'}
            </>
          )}
        </Button>

        {loading && (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !list && !generating && (
          <>
            <Card className="p-8 text-center bg-card border-border" data-testid="grocery-empty-state">
              <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h2 className="text-xl font-bold mb-2">
                {fr ? 'Aucune liste active' : 'No active list'}
              </h2>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                {fr
                  ? "Clique sur 'Generer' et l'IA analysera tes 2 dernieres semaines de repas pour creer ta liste parfaite."
                  : "Click 'Generate' and AI will analyze your last 2 weeks of meals to create your perfect list."}
              </p>
            </Card>

            {/* Show deals even without AI list */}
            {deals && deals.total_deals > 0 && (
              <div className="mt-4" data-testid="deals-standalone">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <Tag className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-wide">
                      {fr ? 'Speciaux de la Semaine' : 'Weekly Deals'}
                    </h3>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-2.5 h-2.5" />
                      {deals.region} — {deals.total_deals} {fr ? 'speciaux' : 'deals'}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {deals.stores.map((store) => (
                    <Card key={store.store} className="bg-card border-border overflow-hidden">
                      <div className="px-3 py-2 flex items-center gap-2 border-b border-border/50" style={{ borderLeftWidth: 4, borderLeftColor: store.color }}>
                        <Store className="w-4 h-4" style={{ color: store.color }} />
                        <span className="text-sm font-black">{store.store}</span>
                        <span className="text-[10px] text-muted-foreground ml-auto">{store.slogan}</span>
                      </div>
                      <div className="px-3 py-2 space-y-1.5">
                        {store.deals.map((deal) => (
                          <div key={deal.deal_id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-medium truncate">{deal.name}</p>
                                {deal.badge && (
                                  <span className="text-[9px] font-bold bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded-md whitespace-nowrap">
                                    {deal.badge}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground">{deal.macro_highlight} · {deal.unit}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-black text-emerald-500">${deal.sale_price.toFixed(2)}</p>
                              <p className="text-[10px] text-muted-foreground line-through">${deal.regular_price.toFixed(2)}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-black text-red-500">-{deal.savings_pct}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Active List */}
        {list && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="grocery-active-list"
          >
            {/* Progress Bar */}
            <Card className="p-4 mb-4 bg-card border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold">
                  {checkedItems}/{totalItems} {fr ? 'items' : 'items'}
                </span>
                <span className="text-sm font-bold text-orange-500">{progress}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-orange-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-muted-foreground">
                  {fr ? `${list.meals_analyzed} repas analyses` : `${list.meals_analyzed} meals analyzed`}
                </span>
                <span className="text-sm font-black text-emerald-500" data-testid="grocery-total-cost">
                  ~${list.total_cost_cad?.toFixed(2)} CAD
                </span>
              </div>
            </Card>

            {/* Summary */}
            {list.weekly_summary && (
              <p className="text-xs text-muted-foreground mb-4 px-1 italic">
                {list.weekly_summary}
              </p>
            )}

            {/* Categories — MA LISTE (shown first, above deals) */}
            <div className="space-y-3">
              {list.categories?.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || Package;
                const color = CATEGORY_COLORS[cat.id] || 'zinc';
                const isExpanded = expandedCats[cat.id] !== false;
                const catChecked = cat.items.filter(i => i.checked).length;

                return (
                  <Card key={cat.id} className="bg-card border-border overflow-hidden" data-testid={`grocery-cat-${cat.id}`}>
                    {/* Category Header */}
                    <button
                      onClick={() => setExpandedCats(p => ({ ...p, [cat.id]: !isExpanded }))}
                      className="w-full p-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-${color}-500/15 flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 text-${color}-500`} />
                      </div>
                      <span className="font-bold text-sm uppercase tracking-wide flex-1 text-left">
                        {cat.name}
                      </span>
                      <span className="text-xs text-muted-foreground mr-2">
                        {catChecked}/{cat.items.length}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                    </button>

                    {/* Items */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-3 pb-3 space-y-1">
                            {cat.items.map((item) => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-3 p-2.5 rounded-lg transition-all ${
                                  item.checked ? 'bg-emerald-500/10' : 'bg-secondary/50 hover:bg-secondary'
                                }`}
                                data-testid={`grocery-item-${item.id}`}
                              >
                                <button
                                  onClick={() => toggleItem(item.id)}
                                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                    item.checked
                                      ? 'bg-emerald-500 border-emerald-500'
                                      : 'border-muted-foreground/30 hover:border-orange-500'
                                  }`}
                                  data-testid={`toggle-${item.id}`}
                                >
                                  {item.checked && <Check className="w-3.5 h-3.5 text-white" />}
                                </button>
                                <div className={`flex-1 min-w-0 ${item.checked ? 'line-through opacity-50' : ''}`}>
                                  <p className="text-sm font-medium truncate">{item.name}</p>
                                  <p className="text-[11px] text-muted-foreground">
                                    {item.quantity} {item.unit}
                                    {item.macro_reason && ` · ${item.macro_reason}`}
                                  </p>
                                </div>
                                <span className="text-xs font-bold text-muted-foreground whitespace-nowrap">
                                  ${item.cost_cad?.toFixed(2) || '0.00'}
                                </span>
                                <button
                                  onClick={() => deleteItem(item.id)}
                                  className="p-1 text-muted-foreground/40 hover:text-red-400 transition-colors"
                                  data-testid={`delete-${item.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}

                            {/* Add Item */}
                            {addingTo === cat.id ? (
                              <div className="flex items-center gap-2 p-2">
                                <input
                                  type="text"
                                  value={newItemName}
                                  onChange={(e) => setNewItemName(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && addItem(cat.id)}
                                  placeholder={fr ? 'Nom de l\'item...' : 'Item name...'}
                                  className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                                  autoFocus
                                  data-testid={`add-input-${cat.id}`}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => addItem(cat.id)}
                                  className="bg-orange-500 hover:bg-orange-600 text-white"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setAddingTo(null); setNewItemName(''); }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setAddingTo(cat.id)}
                                className="w-full p-2 text-xs text-muted-foreground hover:text-orange-500 flex items-center justify-center gap-1 rounded-lg hover:bg-secondary/50 transition-colors"
                                data-testid={`add-to-${cat.id}`}
                              >
                                <Plus className="w-3 h-3" />
                                {fr ? 'Ajouter' : 'Add'}
                              </button>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                );
              })}
            </div>

            {/* Deals Section — below the grocery list */}
            {deals && deals.total_deals > 0 && (
              <div className="mt-4 mb-4" data-testid="deals-section">
                <button
                  onClick={() => setShowDeals(!showDeals)}
                  className="w-full flex items-center gap-2 mb-3 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <Tag className="w-4 h-4 text-red-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-sm font-black uppercase tracking-wide">
                      {fr ? 'Speciaux de la Semaine' : 'Weekly Deals'}
                    </h3>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-2.5 h-2.5" />
                      {deals.region} — {deals.total_deals} {fr ? 'speciaux' : 'deals'}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showDeals ? '' : '-rotate-90'}`} />
                </button>

                <AnimatePresence>
                  {showDeals && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-3">
                        {deals.stores.map((store) => (
                          <Card key={store.store} className="bg-card border-border overflow-hidden" data-testid={`deals-store-${store.store}`}>
                            <div className="px-3 py-2 flex items-center gap-2 border-b border-border/50" style={{ borderLeftWidth: 4, borderLeftColor: store.color }}>
                              <Store className="w-4 h-4" style={{ color: store.color }} />
                              <span className="text-sm font-black">{store.store}</span>
                              <span className="text-[10px] text-muted-foreground ml-auto">{store.slogan}</span>
                            </div>
                            <div className="px-3 py-2 space-y-1.5">
                              {store.deals.map((deal) => (
                                <div key={deal.deal_id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40 hover:bg-secondary/60 transition-colors" data-testid={`deal-${deal.deal_id}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-sm font-medium truncate">{deal.name}</p>
                                      {deal.badge && (
                                        <span className="text-[9px] font-bold bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded-md whitespace-nowrap">
                                          {deal.badge}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground">{deal.macro_highlight} · {deal.unit}</p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-sm font-black text-emerald-500">${deal.sale_price.toFixed(2)}</p>
                                    <p className="text-[10px] text-muted-foreground line-through">${deal.regular_price.toFixed(2)}</p>
                                  </div>
                                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-black text-red-500">-{deal.savings_pct}%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Generated info */}
            <p className="text-[10px] text-muted-foreground text-center mt-4">
              {fr ? 'Généré le' : 'Generated on'}{' '}
              {new Date(list.generated_at).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', {
                weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
              {' · '}{list.profile}
            </p>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
